class DiscoveryByo extends HTMLElement {
  constructor() {
    super();
    this.bundleSize = parseInt(this.getAttribute('data-bundle-size'), 10) || 4;
    this.bundleSlug = this.getAttribute('data-bundle-slug') || 'lyora-discovery-byo';
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.selects = Array.from(this.querySelectorAll('select[data-slot]'));
    this.cta = this.querySelector('[data-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.errorEl = this.querySelector('[data-error]');

    this.selects.forEach((sel) => sel.addEventListener('change', () => this.render()));
    if (this.cta) this.cta.addEventListener('click', (e) => this.onSubmit(e));
    this.render();
  }

  getSelectedItems() {
    return this.selects
      .map((sel) => {
        const opt = sel.selectedOptions[0];
        if (!opt || !opt.value) return null;
        return { variantId: opt.value, title: opt.dataset.title || '' };
      })
      .filter(Boolean);
  }

  render() {
    const items = this.getSelectedItems();
    const complete = items.length === this.bundleSize;

    if (this.cta) this.cta.disabled = !complete || this.isSubmitting;

    if (this.ctaLabel) {
      if (complete) {
        this.ctaLabel.textContent = 'In den Warenkorb';
      } else {
        const remaining = this.bundleSize - items.length;
        this.ctaLabel.textContent = remaining === 1
          ? 'Noch 1 Duft wählen'
          : `Noch ${remaining} Düfte wählen`;
      }
    }

    if (complete) this.setAttribute('data-complete', '');
    else this.removeAttribute('data-complete');

    this.clearError();
  }

  buildItems() {
    // One fresh group ID per add-to-cart click so multiple sets don't merge
    // into the same visual group in the cart drawer.
    const groupId = 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const items = [];
    this.selects.forEach((sel) => {
      const opt = sel.selectedOptions[0];
      if (!opt || !opt.value) return;
      items.push({
        id: opt.value,
        quantity: 1,
        properties: {
          _bundle: this.bundleSlug,
          _bundle_group: groupId,
          Duft: opt.dataset.title || '',
        },
      });
    });
    return items;
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const items = this.buildItems();
    if (items.length !== this.bundleSize) return;

    this.clearError();
    this.isSubmitting = true;
    if (this.cta) this.cta.setAttribute('aria-busy', 'true');
    this.render();

    // Cart-Drawer Section-Render-Hook (Sleek/FoxTheme pattern)
    // Sleek listens for `cart:grouped-sections` — listeners (cart-drawer, cart-icon)
    // push their section IDs so the cart/add response contains rendered HTML.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    const body = JSON.stringify({
      items,
      sections: sectionsToBundle.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const addUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add.js';
      const fetchConfig = (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig)
        ? FoxTheme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' } };

      const response = await fetch(addUrl, { ...fetchConfig, body });
      const parsedState = await response.json();

      // Shopify returns { status, message, description } on rejection
      // (e.g. inventory policy = deny + qty 0, channel restriction, etc.)
      if (parsedState && parsedState.status) {
        console.error('[discovery-byo] cart/add rejected', parsedState);
        const msg = parsedState.description || parsedState.message || `Konnte nicht hinzugefügt werden (Status ${parsedState.status}).`;
        this.showError(msg);
        return;
      }

      if (!response.ok) {
        console.error('[discovery-byo] cart/add non-OK response', response.status, parsedState);
        this.showError(`Konnte nicht hinzugefügt werden (HTTP ${response.status}).`);
        return;
      }

      let cartJson = null;
      try {
        const cartUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart.js';
        const cartFetchConfig = (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig)
          ? FoxTheme.utils.fetchConfig()
          : { method: 'GET', headers: { 'Content-Type': 'application/json' } };
        cartJson = await (await fetch(cartUrl, cartFetchConfig)).json();
        if (cartJson && parsedState && parsedState.sections) {
          cartJson.sections = parsedState.sections;
        }
      } catch (err) {
        console.warn('[discovery-byo] cart fetch failed', err);
      }

      try {
        if (window.FoxTheme && FoxTheme.pubsub && cartJson) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'discovery-byo',
            cart: cartJson,
          });
        }
      } catch (err) {
        console.warn('[discovery-byo] pubsub publish failed', err);
      }

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(this.cta);
        }
      } catch (err) {
        console.warn('[discovery-byo] cartDrawer.show failed', err);
      }

      this.resetSelections();
    } catch (err) {
      console.error('[discovery-byo] unexpected error', err);
      this.showError('Etwas ist schiefgelaufen. Bitte erneut versuchen.');
    } finally {
      this.isSubmitting = false;
      if (this.cta) this.cta.removeAttribute('aria-busy');
      this.render();
    }
  }

  resetSelections() {
    this.selects.forEach((sel) => { sel.value = ''; });
    this.render();
  }
}

if (!customElements.get('discovery-byo')) {
  customElements.define('discovery-byo', DiscoveryByo);
}
